import type { TElement, Value } from "platejs";
import { createPlateEditor } from "platejs/react";
import { MarkdownPlugin } from "@platejs/markdown";

import { proposalPlugins } from "../components/PlateEditor/plugins";
import {
  A4_HEIGHT,
  A4_WIDTH,
  newPageId,
  type PlatePage,
} from "../components/PlateEditor/types";
import type { Section } from "../../../../../../../features/scopingAi/types/documentTypes";

export type BrandingForPlate = {
  primary_color?: string;
  secondary_color?: string;
  font_color?: string;
  font_family?: string;
  logo_url?: string;
  tagline?: string;
  brand_title?: string;
};

const buildLogoNode = (logoUrl: string): TElement => ({
  type: "img",
  url: logoUrl,
  textAlign: "center",
  children: [{ text: "" }],
});

const buildTitleNode = (title: string): TElement => ({
  type: "h1",
  textAlign: "center",
  children: [{ text: title }],
});

const buildSectionTitleNode = (title: string): TElement => ({
  type: "h2",
  children: [{ text: title }],
});

const emptyParagraph = (): TElement => ({
  type: "p",
  children: [{ text: "" }],
});

const newDeserializerEditor = () =>
  createPlateEditor({ plugins: proposalPlugins });

const deserializeMarkdown = (markdown: string): Value => {
  if (!markdown || markdown.trim().length === 0) {
    return [emptyParagraph()];
  }
  try {
    const editor = newDeserializerEditor();
    const api = editor.getApi(MarkdownPlugin);
    const value = api.markdown.deserialize(markdown);
    return value.length > 0 ? value : [emptyParagraph()];
  } catch (err) {
    console.error("[markdownToPlate] deserialize failed:", err);
    return [{ type: "p", children: [{ text: markdown }] }];
  }
};

const makePage = (
  name: string,
  plateValue: Value,
  background: PlatePage["background"] = { type: "solid", color: "#ffffff" },
): PlatePage => ({
  id: newPageId(),
  name,
  canvasSize: { width: A4_WIDTH, height: A4_HEIGHT },
  background,
  plateValue,
});

/**
 * Returns true when the markdown content begins with an ATX heading
 * (`#`–`######`). Used to decide whether to synthesize an h2 from the
 * `section.title` field, or trust the heading already in `section.content`.
 * Without this check, a section whose content starts with `## Purpose` would
 * render the heading twice — once from `buildSectionTitleNode(section.title)`
 * and once from the markdown deserializer.
 */
const startsWithHeading = (content: string): boolean =>
  /^\s*#{1,6}\s+\S/.test(content);

// ─── Pagination ──────────────────────────────────────────────────────────────
//
// We split the AI's flat node list into A4-shaped pages at ingest time, using
// per-node-type height estimates. This is NOT live auto-pagination — once a
// page is created, typing into it does not reflow content into the next page.
// True auto-reflow would require DOM measurement + cross-editor content moves,
// which Plate/Slate doesn't support natively. For the AI ingestion path the
// estimate is good enough: long policies split into multiple readable pages
// instead of one giant scroll.

// A4 (794×1123 at 96 DPI) minus our 56px page padding (top + bottom).
const A4_USABLE_HEIGHT = A4_HEIGHT - 56 * 2;

// Approximate characters per line at A4 content width (794 - 56*2 ≈ 682px),
// 14px font, ~9px average char width.
const CHARS_PER_LINE = 80;
const LINE_HEIGHT = 24;

const extractText = (node: unknown): string => {
  if (!node || typeof node !== "object") return "";
  const n = node as { text?: unknown; children?: unknown[] };
  if (typeof n.text === "string") return n.text;
  if (Array.isArray(n.children)) {
    return n.children.map(extractText).join("");
  }
  return "";
};

const estimateBlockHeight = (node: TElement): number => {
  const t = (node as { type?: string }).type;
  switch (t) {
    case "h1":
      return 60; // text-3xl + mt-6 mb-3
    case "h2":
      return 50; // text-2xl + mt-5 mb-2
    case "h3":
      return 40; // text-xl + mt-4 mb-2
    case "blockquote":
    case "p": {
      const text = extractText(node);
      const lines = Math.max(1, Math.ceil(text.length / CHARS_PER_LINE));
      return lines * LINE_HEIGHT + 16; // my-2 wrapper
    }
    case "list": {
      const items = Array.isArray((node as { children?: unknown[] }).children)
        ? ((node as { children: unknown[] }).children as unknown[])
        : [];
      // sum each item's wrapped-line cost; bullets/li have my-1 + line-height
      const itemHeight = items.reduce((sum, item) => {
        const txt = extractText(item);
        const lines = Math.max(1, Math.ceil(txt.length / (CHARS_PER_LINE - 4)));
        return sum + lines * LINE_HEIGHT + 8;
      }, 0);
      return itemHeight + 16; // my-2 wrapper around the list
    }
    case "table": {
      const rows = Array.isArray((node as { children?: unknown[] }).children)
        ? ((node as { children: unknown[] }).children as unknown[]).length
        : 1;
      return rows * 40 + 16; // ~40px per row + margin
    }
    case "code_block":
      return 80;
    case "hr":
      return 24;
    case "img":
      return 120; // centred logo (maxHeight 100px) + my-3 margin
    default:
      return 32;
  }
};

/**
 * Split a flat node list into chunks that each fit within `maxHeight`.
 * Always emits at least one page, even for empty input. A single oversized
 * node (e.g. an enormous table) goes alone on its own page rather than being
 * dropped or split mid-element.
 */
const paginate = (
  nodes: Value,
  maxHeight: number = A4_USABLE_HEIGHT,
): Value[] => {
  if (nodes.length === 0) return [[]];
  const pages: Value[] = [[]];
  let used = 0;
  for (const node of nodes) {
    const h = estimateBlockHeight(node as TElement);
    const currentPage = pages[pages.length - 1];
    if (currentPage.length > 0 && used + h > maxHeight) {
      pages.push([]);
      used = 0;
    }
    pages[pages.length - 1].push(node);
    used += h;
  }
  return pages;
};

/**
 * Build paginated Plate pages from AI-generated sections + branding.
 *
 * Structure:
 *   1. Cover page  — logo, policy title, org name, date (one fixed page)
 *   2. TOC page    — numbered list of all section titles (one fixed page)
 *   3. Content pages — sections numbered "1. Purpose and Scope" etc.,
 *                      paginated by estimated rendered height
 */
/**
 * Build a standalone Cover page from branding + title.
 * Extracted from sectionsToPlatePages so the regen flow can re-add a cover
 * page to an existing page list (without rebuilding from sections).
 *
 * Layout: top spacers → logo (centred) → org name → mid spacers → policy title → date
 */
export const buildCoverPage = (
  branding: BrandingForPlate | null,
  title: string,
): PlatePage => {
  const logoUrl = branding?.logo_url;
  const orgName = branding?.brand_title;
  const coverNodes: Value = [];
  for (let i = 0; i < 5; i++) coverNodes.push(emptyParagraph());
  if (logoUrl) coverNodes.push(buildLogoNode(logoUrl));
  if (orgName) {
    coverNodes.push({ type: "p", textAlign: "center", children: [{ text: orgName }] } as TElement);
  }
  for (let i = 0; i < 3; i++) coverNodes.push(emptyParagraph());
  if (title) coverNodes.push(buildTitleNode(title));
  coverNodes.push(emptyParagraph());
  const today = new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });
  coverNodes.push({ type: "p", textAlign: "center", children: [{ text: today }] } as TElement);
  return makePage(title || "Cover", coverNodes.length > 0 ? coverNodes : [emptyParagraph()]);
};

/**
 * Build a standalone Table of Contents page from an ordered list of section
 * titles. Extracted from sectionsToPlatePages so the regen flow can re-add a
 * TOC page rebuilt from the CURRENT content-page headings (preserving edits).
 */
export const buildTocPage = (sectionTitles: string[]): PlatePage => {
  // All TOC text at 18px (1.125em × 16px base) so it scales with zoom.
  const TOC_SIZE = "1.125em";
  const tocNodes: Value = [];
  tocNodes.push({
    type: "p",
    children: [{ text: "Table of Contents", bold: true, fontSize: TOC_SIZE }],
  } as TElement);
  tocNodes.push(emptyParagraph());
  sectionTitles.forEach((cleanTitle, idx) => {
    tocNodes.push({
      type: "p",
      children: [{ text: `${idx + 1}. ${cleanTitle}`, fontSize: TOC_SIZE }],
    } as TElement);
  });
  return makePage("Table of Contents", tocNodes.length > 0 ? tocNodes : [emptyParagraph()]);
};

export const sectionsToPlatePages = (
  sections: Section[] | undefined,
  branding: BrandingForPlate | null,
  title: string,
): PlatePage[] => {
  const safeSections = sections ?? [];
  const sectionTitles = safeSections.map((section, idx) =>
    (section.title ?? "")
      .replace(/^#+\s*/, "")
      .replace(/^\d+[\.\)]\s*/, "")
      .trim() || `Section ${idx + 1}`,
  );

  const coverPage = buildCoverPage(branding, title);
  const tocPage = buildTocPage(sectionTitles);

  // ── Content pages ─────────────────────────────────────────────────────────
  const allContentNodes: Value = [];

  for (let idx = 0; idx < safeSections.length; idx++) {
    const section = safeSections[idx];
    const num = idx + 1;
    const cleanTitle = (section.title ?? "")
      .replace(/^#+\s*/, "")       // strip markdown heading markers
      .replace(/^\d+[\.\)]\s*/, "") // strip existing number prefix e.g. "9. " or "9) "
      .trim() || `Section ${num}`;
    const numberedTitle = `${num}. ${cleanTitle}`;
    const trimmed = (section.content ?? "").trim();

    allContentNodes.push(buildSectionTitleNode(numberedTitle));

    // If content opens with a heading, strip that first line to avoid duplication
    // with the numbered title we just pushed above.
    let contentToDeserialize = trimmed;
    if (startsWithHeading(trimmed)) {
      const firstNewline = trimmed.indexOf("\n");
      contentToDeserialize = firstNewline !== -1 ? trimmed.slice(firstNewline + 1).trim() : "";
    }

    if (contentToDeserialize) {
      const sectionNodes = deserializeMarkdown(contentToDeserialize);
      allContentNodes.push(...sectionNodes);
    }
  }

  if (allContentNodes.length === 0) allContentNodes.push(emptyParagraph());

  const pageGroups = paginate(allContentNodes);
  const contentPages = pageGroups.map((nodes, idx) =>
    makePage(`Page ${idx + 3}`, nodes),
  );

  return [coverPage, tocPage, ...contentPages];
};

/**
 * Convert a flat markdown string into paginated PlatePage[].
 * Used by PolicyDocuments and PolicyGenerationStep so they can use the same
 * PlateJS-based export as BrandedProposalEditor.
 *
 * NOTE: Branded editor whole-document regeneration must use `sectionsToPlatePages`
 * instead — this helper does not produce a cover page or Table of Contents.
 */
export const markdownToPlatePages = (
  markdown: string,
  title: string,
  branding: BrandingForPlate | null,
): PlatePage[] => {
  const logoUrl = branding?.logo_url;
  const allNodes: Value = [];
  if (logoUrl) allNodes.push(buildLogoNode(logoUrl));
  if (title) allNodes.push(buildTitleNode(title));
  const content = deserializeMarkdown(markdown.trim());
  allNodes.push(...content);
  if (allNodes.length === 0) allNodes.push(emptyParagraph());
  const pageGroups = paginate(allNodes);
  return pageGroups.map((nodes, idx) =>
    makePage(idx === 0 && title ? title : `Page ${idx + 1}`, nodes),
  );
};
